/**
 * AppointmentExam Service
 * API integration for appointment complementary exams
 */

import apiClient from './apiClient';
import type {
  AppointmentExam,
  SuggestedExam,
  UpdateExamResultData,
} from '../types/appointmentExam';

class AppointmentExamService {
  private getBaseUrl(appointmentId: number): string {
    return `/appointments/${appointmentId}/exams`;
  }

  async getExamsByAppointment(appointmentId: number): Promise<AppointmentExam[]> {
    const response = await apiClient.get(this.getBaseUrl(appointmentId));
    return response.data?.data ?? response.data ?? [];
  }

  async addExams(appointmentId: number, examIds: number[]): Promise<AppointmentExam[]> {
    const response = await apiClient.post(this.getBaseUrl(appointmentId), { examIds });
    return response.data?.data ?? response.data ?? [];
  }

  async syncExams(appointmentId: number, examIds: number[]): Promise<AppointmentExam[]> {
    const response = await apiClient.put(`${this.getBaseUrl(appointmentId)}/sync`, { examIds });
    return response.data?.data ?? response.data ?? [];
  }

  async updateResult(appointmentId: number, examId: number, data: UpdateExamResultData): Promise<AppointmentExam> {
    const response = await apiClient.put(`${this.getBaseUrl(appointmentId)}/${examId}`, data);
    return response.data?.data ?? response.data;
  }

  async uploadFile(appointmentId: number, examId: number, file: File): Promise<AppointmentExam> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      `${this.getBaseUrl(appointmentId)}/${examId}/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data?.data ?? response.data;
  }

  async deleteFile(appointmentId: number, examId: number): Promise<AppointmentExam> {
    const response = await apiClient.delete(`${this.getBaseUrl(appointmentId)}/${examId}/file`);
    return response.data?.data ?? response.data;
  }

  getDownloadUrl(appointmentId: number, examId: number): string {
    return `/api/v1/appointments/${appointmentId}/exams/${examId}/download`;
  }

  async getSuggestions(employeeId: number, examType: string): Promise<SuggestedExam[]> {
    const response = await apiClient.get('/appointments/exams/suggestions', {
      params: { employeeId, examType },
    });
    return response.data?.data ?? response.data ?? [];
  }
  async checkClinicCoverage(
    clinicId: number,
    examIds: number[],
    companyId: number
  ): Promise<{
    covered: number[];
    uncovered: Array<{ examId: number; examName: string }>;
    alternativeClinics: Record<number, Array<{ clinicId: number; clinicName: string; price: number | null; turnaroundDays: number | null }>>;
  }> {
    const response = await apiClient.get('/appointments/check-clinic-coverage', {
      params: { clinicId, examIds: examIds.join(','), companyId },
    });
    return response.data?.data ?? response.data;
  }

  async getReferralExams(appointmentId: number): Promise<AppointmentExam[]> {
    const response = await apiClient.get(`/appointments/${appointmentId}/referral-exams`);
    return response.data?.data ?? response.data ?? [];
  }

  async getReferrals(appointmentId: number): Promise<AppointmentExam[]> {
    const response = await apiClient.get(`/appointments/${appointmentId}/referrals`);
    return response.data?.data ?? response.data ?? [];
  }

  async syncReferrals(
    appointmentId: number,
    assignments: Array<{ examId: number; referralClinicId: number }>
  ): Promise<void> {
    await apiClient.put(`${this.getBaseUrl(appointmentId)}/sync-referrals`, { assignments });
  }

  async sendReferrals(appointmentId: number): Promise<void> {
    await apiClient.post(`/appointments/${appointmentId}/exams/send-referrals`);
  }
}

export const appointmentExamService = new AppointmentExamService();
export default appointmentExamService;
